import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, formattedCurrency, withStyles, Card, FedwireTransactionResponse, TransactionType } from 'vopay';
import { TransactionCard, Title, DetailsRows } from 'UI';
import BankDetailsCard from './Card/BankDetailsCard';
import RecipientDetailsCard from './Card/RecipientDetailsCard';
import ApprovalHistoryDetailsCard from './Card/ApprovalHistoryDetailsCard';
import styles from '../Transactions.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import useElinxDetails from './hooks/useElinxDetails';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: FedwireTransactionResponse;
  Notes: string;
  jwt: string;
  ELinxRequestID?: string;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
  accountId: string;
};

const FedwireFundingTransactionInfo: FC<TransactionInfoProps> = ({ transaction, Notes, jwt, ELinxRequestID = '' }) => {
  const { state } = useLocation<DataProps | undefined>();
  const {
    response: { JWT: mainJwt },
  } = useClientData();
  const isMainAccount = !jwt || jwt === mainJwt;
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.FedwireFundingTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const receiverName = transaction.FirstName ? transaction.FirstName + transaction.LastName : transaction.CompanyName;

  const { elinxAdditionalId, invoiceButton } = useElinxDetails({ ELinxRequestID, jwt });

  const transactionDetails = [
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Transaction.TransactionHistory.transactionTypes.Fedwire Funding', {
        defaultValue: 'Fedwire Funding',
      }),
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.holdReleaseDate', { defaultValue: 'Hold Release Date' }),
      value: transaction.HoldReleaseDateTime,
    },
    {
      label: t('TransactionDetails.amountRequested', { defaultValue: 'Amount Requested' }),
      value: transaction.Currency + ' ' + formattedCurrency(transaction.Amount),
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.wireReferenceNumber', { defaultValue: 'Wire Reference Number' }),
      value: transaction.WireReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: Notes || '',
    },
    {
      label: t('TransactionDetails.invoice', { defaultValue: 'Invoice' }),
      value: invoiceButton,
      visible: !!invoiceButton,
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={receiverName || ''}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        type={'Fedwire Funding'}
        contactId={state?.accountId}
        failureReason={state?.failureReason || ''}
        jwt={jwt}
        isMainAccount={isMainAccount}
        additionalId={elinxAdditionalId}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsHeader', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <ApprovalHistoryDetailsCard
        transactionID={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={TransactionType.FEDWIRE_FUNDING}
        jwt={jwt}
      />
      <RecipientDetailsCard
        receiverName={receiverName || ''}
        accountID={state?.accountId}
        address={transaction}
        isMainAccount={isMainAccount}
      />
      <BankDetailsCard
        details={{
          AccountNumber: transaction.AccountNumber,
          ABARoutingNumber: transaction.ABARoutingNumber,
        }}
      />

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoHeader', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default FedwireFundingTransactionInfo;
